import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable } from '@angular/core';
import { DisclaimerModalComponent } from '../../../shared/components/disclaimer-modal/disclaimer-modal.component';
import { ConfirmRemoveMonitoredUsersModalComponent } from '../../../views/users-ui/components/confirm-remove-monitored-user-modal/confirm-remove-monitored-user-modal.component';

import { ConfirmUserInvitationModalComponent } from '../../../core/modals/components/confirm-user-invitation-modal/confirm-user-invitation-modal.component';
import { ConsentAgreementModalComponent } from '../../../shared/components/consent-agreement-modal/consent-agreement-modal.component';
import { UserInvitation } from '../../../shared/models/users/monitored-invitation.model';
import { ChangeMonitoredUserFlowRequest } from '../../../shared/models/users/requests/change-monitored-user-flow-request.model';
import { ConfirmChangeFlowModalComponent } from '../../../views/users-ui/components/user-details/monitored-user-flow/confirm-change-flow-modal/confirm-change-flow-modal.component';
import { InviteMonitoredUserComponent } from '../../../views/users-ui/components/users-list/invite-monitored-user/invite-monitored-user.component';
import { ModalsActions } from '../actions/modals.actions';
import { BaseModalComponent } from '../components/base-modal/base-modal.component';
import { DefaultModalConfig } from '../components/base-modal/default-modal-config.component';
import { JointResponsibilityAgreementComponent } from '../../../shared/components/joint-responsibility-agreement/joint-responsibility-agreement.component';
import { ConfirmDeleteUserModalComponent } from '../../../views/admin-ui/components/user-details/confirm-delete-user-modal/confirm-delete-user-modal.component';

@Injectable()
export class ModalsService {
  private confirmRemoveMonitoredUsersModal: OverlayRef | null = null;
  private confirmChangeFlowModal: OverlayRef | null = null;
  private disclaimerModal: OverlayRef | null = null;
  private jointResponsibilityAgreementModal: OverlayRef | null = null;
  private consentAgreementModal: OverlayRef | null = null;
  private inviteMonitoredUserModal: OverlayRef | null = null;
  private confirmDeleteUserModal: OverlayRef | null = null;
  private confirmMonitoredUserModal: Map<string, OverlayRef> = new Map<string, OverlayRef>();

  constructor(private readonly overlay: Overlay) {}

  public openConfirmRemoveMonitoredUsersModal(users: string[]): void {
    this.confirmRemoveMonitoredUsersModal = this.createDefaultModal({
      componentType: ConfirmRemoveMonitoredUsersModalComponent,
      data: users,
      closeAction: ModalsActions.closeConfirmRemoveMonitoredUsersModal(),
      closeOnBackdropClick: false,
      specificPanelClass: ['remove-user-modal-body'],
    });
  }

  public openConfirmChangeFlowModal(request: ChangeMonitoredUserFlowRequest): void {
    this.confirmChangeFlowModal = this.createDefaultModal({
      componentType: ConfirmChangeFlowModalComponent,
      data: request,
      closeAction: ModalsActions.closeConfirmChangeFlowModal(),
      closeOnBackdropClick: false,
      specificPanelClass: ['remove-user-modal-body'],
    });
  }

  public closeConfirmRemoveMonitoredUsersModal(): void {
    this.confirmRemoveMonitoredUsersModal?.dispose();
    this.confirmRemoveMonitoredUsersModal = null;
  }

  public closeConfirmChangeFlowModal(): void {
    this.confirmChangeFlowModal?.dispose();
    this.confirmChangeFlowModal = null;
  }

  public openDisclaimerModal(): void {
    this.disclaimerModal = this.createDefaultModal({
      componentType: DisclaimerModalComponent,
      data: {},
      closeAction: ModalsActions.closeDisclaimerModal(),
    });
  }

  public openJointResponsibilityAgreementModal(): void {
    this.jointResponsibilityAgreementModal = this.createDefaultModal({
      componentType: JointResponsibilityAgreementComponent,
      data: {},
      closeAction: ModalsActions.closeJointResponsibilityAgreementModal(),
    });
  }

  public closeDisclaimerModal(): void {
    this.disclaimerModal?.dispose();
    this.disclaimerModal = null;
  }

  public closeJointResponsibilityAgreementModal(): void {
    this.jointResponsibilityAgreementModal?.dispose();
    this.jointResponsibilityAgreementModal = null;
  }

  public openConsentAgreementModal(): void {
    this.consentAgreementModal = this.createDefaultModal({
      componentType: ConsentAgreementModalComponent,
      data: {},
      closeAction: ModalsActions.closeConsentAgreementModal(),
    });
  }

  public closeConsentAgreementModal(): void {
    this.consentAgreementModal?.dispose();
    this.consentAgreementModal = null;
  }

  public openInviteMonitoredUserModal(): void {
    this.inviteMonitoredUserModal = this.createDefaultModal({
      componentType: InviteMonitoredUserComponent,
      data: {},
      closeAction: ModalsActions.closeInviteMonitoredUserModal(),
    });
  }

  public closeInviteMonitoredUserModal(): void {
    this.inviteMonitoredUserModal?.dispose();
    this.inviteMonitoredUserModal = null;
  }

  public openConfirmMonitoredUserInvitation(data: UserInvitation): void {
    this.confirmMonitoredUserModal.set(
      data.id,
      this.createDefaultModal({
        componentType: ConfirmUserInvitationModalComponent,
        data: data,
        closeAction: ModalsActions.closeConfirmUserInvitationModal({ id: data.id }),
      })
    );
  }

  public closeConfirmMonitoredUserInvitation(id: string): void {
    const modal = this.confirmMonitoredUserModal.get(id);
    modal?.dispose();
    this.confirmMonitoredUserModal.delete(id);
  }

  public openConfirmDeleteUserModal(userId: string): void {
    this.confirmDeleteUserModal = this.createDefaultModal({
      componentType: ConfirmDeleteUserModalComponent,
      data: userId,
      closeAction: ModalsActions.closeConfirmDeleteUserModal(),
      closeOnBackdropClick: false,
      specificPanelClass: ['remove-user-modal-body'],
    });
  }

  public closeConfirmDeleteUserModal(): void {
    this.confirmDeleteUserModal?.dispose();
    this.confirmDeleteUserModal = null;
  }

  private createDefaultModal<TComponent extends BaseModalComponent<TData>, TData>({
    componentType,
    data,
    specificPanelClass,
    closeAction,
    closeOnBackdropClick,
  }: DefaultModalConfig<TComponent, TData>): OverlayRef {
    const overlayRef = this.overlay.create(this.getDefaultConfigForBaseModal(specificPanelClass));
    const portal = new ComponentPortal(componentType);
    const componentRef = overlayRef.attach(portal);
    componentRef.instance.data = data;
    componentRef.instance.closeAction = closeAction;
    if (closeOnBackdropClick) {
      this.registerDefaultBackdropClickBehaviour(overlayRef, componentRef);
    }
    return overlayRef;
  }

  private registerDefaultBackdropClickBehaviour<T>(
    overlay: OverlayRef,
    componentRef: ComponentRef<BaseModalComponent<T>>
  ): void {
    overlay.backdropClick().subscribe(() => {
      componentRef.instance.close();
    });
  }

  private getDefaultConfigForBaseModal(specificPanelClass?: string[]): OverlayConfig {
    return {
      disposeOnNavigation: true,
      hasBackdrop: true,
      panelClass: specificPanelClass
        ? ['default-modal-body', ...specificPanelClass]
        : 'default-modal-body',
      backdropClass: 'default-modal-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
    };
  }
}
